"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type SwiperClass from "swiper";
const SWIPE_TRIGGER_THRESHOLD = 32;
const MAX_SWIPE_OFFSET = 132;
const SWIPE_EXIT_DURATION_MS = 220;
const SWIPE_EXIT_OFFSET = 420;
const SWIPE_EXIT_ROTATION_DEGREES = 3;
const QUICK_VOTE_TRANSFORM_DATA_ATTRIBUTE = "quickVoteTransform";
const QUICK_VOTE_COMPUTED_STYLE_PATCH_FLAG = "__memesQuickVoteComputedStylePatched";
type TimeoutHandle = ReturnType<typeof globalThis.setTimeout>;
type SwipeDirection = "left" | "right";
type TouchLikeEvent = {
  readonly changedTouches?: ArrayLike<{
    readonly clientX?: number;
    readonly pageX?: number;
  }> | undefined;
  readonly targetTouches?: ArrayLike<{
    readonly clientX?: number;
    readonly pageX?: number;
  }> | undefined;
  readonly touches?: ArrayLike<{
    readonly clientX?: number;
    readonly pageX?: number;
  }> | undefined;
  readonly stopPropagation?: () => void;
};
interface UseMemesQuickVotePreviewSwipeArgs {
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
  readonly swipeVoteAmount: number | null;
}
interface UseMemesQuickVotePreviewSwipeResult {
  readonly previewCardRef: React.RefObject<HTMLElement | null>;
  readonly canUseSwiperTouchSurface: boolean;
  readonly cardTransform: string | undefined;
  readonly cardTransitionDuration: string;
  readonly swipeOffset: number;
  readonly handleCardTransitionEnd: (event: React.TransitionEvent<HTMLElement>) => void;
  readonly handleSwiperMove: (swiper: SwiperClass) => void;
  readonly handleSwiperTouchEnd: (swiper: SwiperClass) => void;
}
interface SwipeState {
  readonly fallbackTouchCurrentXRef: React.RefObject<number | null>;
  readonly fallbackTouchStartXRef: React.RefObject<number | null>;
  readonly handleCardTransitionEnd: (event: React.TransitionEvent<HTMLElement>) => void;
  readonly handleSwipeEnd: (swipeDistance: number) => void;
  readonly handleSwiperMove: (swiper: SwiperClass) => void;
  readonly handleSwiperTouchEnd: (swiper: SwiperClass) => void;
  readonly isFallbackTouchActiveRef: React.RefObject<boolean>;
  readonly resetFallbackTouch: () => void;
  readonly resetSwipe: () => void;
  readonly setSwipeOffset: React.Dispatch<React.SetStateAction<number>>;
  readonly swipeExitDirection: SwipeDirection | null;
  readonly swipeOffset: number;
}
function commitSwipeAction(direction: SwipeDirection, onSkip: () => void, onVoteWithSwipe: () => void) {
  if (direction === "left") {
    onSkip();
    return;
  }
  onVoteWithSwipe();
}
const getTouchClientX = (touch: {
  readonly clientX?: number;
  readonly pageX?: number;
} | undefined): number | null => {
  if (typeof touch?.pageX === "number") {
    return touch.pageX;
  }
  if (typeof touch?.clientX === "number") {
    return touch.clientX;
  }
  return null;
};
const getTouchListClientX = (touches: ArrayLike<{
  readonly clientX?: number;
  readonly pageX?: number;
}> | undefined): number | null => {
  if (!touches || touches.length === 0) {
    return null;
  }
  return getTouchClientX(touches[0]);
};
const getTouchEventClientX = (event: TouchLikeEvent): number | null => getTouchListClientX(event.touches) ?? getTouchListClientX(event.targetTouches) ?? getTouchListClientX(event.changedTouches);
function getCardTransform(isMobile: boolean, swipeExitDirection: SwipeDirection | null, swipeOffset: number) {
  if (!isMobile) {
    return undefined;
  }
  if (swipeExitDirection === "left") {
    return `translateX(${Math.min(swipeOffset, 0) - SWIPE_EXIT_OFFSET}px) rotate(-${SWIPE_EXIT_ROTATION_DEGREES}deg)`;
  }
  if (swipeExitDirection === "right") {
    return `translateX(${Math.max(swipeOffset, 0) + SWIPE_EXIT_OFFSET}px) rotate(${SWIPE_EXIT_ROTATION_DEGREES}deg)`;
  }
  return `translateX(${swipeOffset}px)`;
}
function getCardTransitionDuration(swipeExitDirection: SwipeDirection | null, swipeOffset: number) {
  if (swipeExitDirection === null && swipeOffset !== 0) {
    return "0ms";
  }
  return `${SWIPE_EXIT_DURATION_MS}ms`;
}
function patchComputedStyleForFallbackSwipe() {
  if (globalThis.window === undefined || typeof globalThis.window.Touch === "function") {
    return;
  }
  const browserWindow = globalThis.window;
  const patchedWindow = browserWindow as typeof browserWindow & {
    [QUICK_VOTE_COMPUTED_STYLE_PATCH_FLAG]?: boolean;
  };
  if (patchedWindow[QUICK_VOTE_COMPUTED_STYLE_PATCH_FLAG]) {
    return;
  }
  const originalGetComputedStyle = browserWindow.getComputedStyle.bind(browserWindow);
  browserWindow.getComputedStyle = ((element: Element, pseudoElement?: string) => {
    const computedStyle = originalGetComputedStyle(element, pseudoElement);
    if (!(element instanceof HTMLElement)) {
      return computedStyle;
    }
    const transform = element.dataset[QUICK_VOTE_TRANSFORM_DATA_ATTRIBUTE] ?? "";
    if (transform.length === 0) {
      return computedStyle;
    }
    const patchedComputedStyle = Object.create(computedStyle) as CSSStyleDeclaration;
    Object.defineProperty(patchedComputedStyle, "transform", {
      configurable: true,
      value: transform
    });
    patchedComputedStyle.getPropertyValue = (property: string) => property === "transform" ? transform : computedStyle.getPropertyValue(property);
    return patchedComputedStyle;
  }) as typeof browserWindow.getComputedStyle;
  patchedWindow[QUICK_VOTE_COMPUTED_STYLE_PATCH_FLAG] = true;
}
function useSwipeCommitTimeoutCleanup(clearSwipeCommitTimeout) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== clearSwipeCommitTimeout) {
    t0 = () => () => {
      clearSwipeCommitTimeout();
    };
    t1 = [clearSwipeCommitTimeout];
    $[0] = clearSwipeCommitTimeout;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
}
function useQuickVotePreviewSwipeState(t0) {
  const $ = _c(36);
  const {
    isBusy,
    isMobile,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    swipeVoteAmount,
    usesTransitionlessSwipeCommit
  } = t0;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeExitDirection, setSwipeExitDirection] = useState(null);
  const fallbackTouchStartXRef = useRef(null);
  const fallbackTouchCurrentXRef = useRef(null);
  const isFallbackTouchActiveRef = useRef(false);
  const swipeCommitTimeoutRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (swipeCommitTimeoutRef.current === null) {
        return;
      }
      globalThis.clearTimeout(swipeCommitTimeoutRef.current);
      swipeCommitTimeoutRef.current = null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearSwipeCommitTimeout = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      fallbackTouchStartXRef.current = null;
      fallbackTouchCurrentXRef.current = null;
      isFallbackTouchActiveRef.current = false;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resetFallbackTouch = t2;
  let t3;
  if ($[2] !== swipeExitDirection) {
    t3 = () => {
      if (swipeExitDirection !== null) {
        return;
      }
      flushSync(() => {
        setSwipeOffset(0);
      });
    };
    $[2] = swipeExitDirection;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resetSwipe = t3;
  let t4;
  if ($[4] !== onAdvanceStart || $[5] !== onSkip || $[6] !== onVoteWithSwipe || $[7] !== swipeExitDirection || $[8] !== usesTransitionlessSwipeCommit) {
    t4 = direction => {
      if (swipeExitDirection !== null) {
        return;
      }
      clearSwipeCommitTimeout();
      resetFallbackTouch();
      onAdvanceStart();
      flushSync(() => {
        setSwipeExitDirection(direction);
      });
      if (!usesTransitionlessSwipeCommit) {
        return;
      }
      swipeCommitTimeoutRef.current = globalThis.setTimeout(() => {
        swipeCommitTimeoutRef.current = null;
        commitSwipeAction(direction, onSkip, onVoteWithSwipe);
      }, SWIPE_EXIT_DURATION_MS);
    };
    $[4] = onAdvanceStart;
    $[5] = onSkip;
    $[6] = onVoteWithSwipe;
    $[7] = swipeExitDirection;
    $[8] = usesTransitionlessSwipeCommit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const beginSwipeCommit = t4;
  let t5;
  if ($[10] !== beginSwipeCommit || $[11] !== resetSwipe || $[12] !== swipeVoteAmount) {
    t5 = swipeDistance => {
      resetFallbackTouch();
      if (swipeDistance <= -SWIPE_TRIGGER_THRESHOLD) {
        beginSwipeCommit("left");
        return;
      }
      if (swipeDistance >= SWIPE_TRIGGER_THRESHOLD && swipeVoteAmount !== null) {
        beginSwipeCommit("right");
        return;
      }
      resetSwipe();
    };
    $[10] = beginSwipeCommit;
    $[11] = resetSwipe;
    $[12] = swipeVoteAmount;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSwipeEnd = t5;
  let t6;
  if ($[14] !== isBusy || $[15] !== isMobile || $[16] !== swipeExitDirection) {
    t6 = swiper => {
      if (isBusy || !isMobile || swipeExitDirection !== null) {
        return;
      }
      setSwipeOffset(Math.max(-MAX_SWIPE_OFFSET, Math.min(swiper.touches.diff, MAX_SWIPE_OFFSET)));
    };
    $[14] = isBusy;
    $[15] = isMobile;
    $[16] = swipeExitDirection;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleSwiperMove = t6;
  let t7;
  if ($[18] !== handleSwipeEnd || $[19] !== isBusy || $[20] !== isMobile || $[21] !== resetSwipe) {
    t7 = swiper_0 => {
      if (isBusy || !isMobile) {
        resetSwipe();
        return;
      }
      handleSwipeEnd(swiper_0.touches.diff);
    };
    $[18] = handleSwipeEnd;
    $[19] = isBusy;
    $[20] = isMobile;
    $[21] = resetSwipe;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleSwiperTouchEnd = t7;
  let t8;
  if ($[23] !== onSkip || $[24] !== onVoteWithSwipe || $[25] !== swipeExitDirection || $[26] !== usesTransitionlessSwipeCommit) {
    t8 = event => {
      if (event.target !== event.currentTarget || event.propertyName !== "transform" || swipeExitDirection === null) {
        return;
      }
      if (usesTransitionlessSwipeCommit) {
        return;
      }
      commitSwipeAction(swipeExitDirection, onSkip, onVoteWithSwipe);
    };
    $[23] = onSkip;
    $[24] = onVoteWithSwipe;
    $[25] = swipeExitDirection;
    $[26] = usesTransitionlessSwipeCommit;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleCardTransitionEnd = t8;
  useSwipeCommitTimeoutCleanup(clearSwipeCommitTimeout);
  let t9;
  if ($[28] !== handleCardTransitionEnd || $[29] !== handleSwipeEnd || $[30] !== handleSwiperMove || $[31] !== handleSwiperTouchEnd || $[32] !== resetSwipe || $[33] !== swipeExitDirection || $[34] !== swipeOffset) {
    t9 = {
      fallbackTouchCurrentXRef,
      fallbackTouchStartXRef,
      handleCardTransitionEnd,
      handleSwipeEnd,
      handleSwiperMove,
      handleSwiperTouchEnd,
      isFallbackTouchActiveRef,
      resetFallbackTouch,
      resetSwipe,
      setSwipeOffset,
      swipeExitDirection,
      swipeOffset
    };
    $[28] = handleCardTransitionEnd;
    $[29] = handleSwipeEnd;
    $[30] = handleSwiperMove;
    $[31] = handleSwiperTouchEnd;
    $[32] = resetSwipe;
    $[33] = swipeExitDirection;
    $[34] = swipeOffset;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}
export default function useMemesQuickVotePreviewSwipe(t0) {
  const $ = _c(33);
  const {
    isBusy,
    isMobile,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe,
    swipeVoteAmount
  } = t0;
  const previewCardRef = useRef(null);
  const [canUseSwiperTouchSurface, setCanUseSwiperTouchSurface] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isMobile) {
    t1 = () => {
      if (!isMobile) {
        setCanUseSwiperTouchSurface(false);
        return;
      }
      setCanUseSwiperTouchSurface(globalThis.window !== undefined && typeof globalThis.window.Touch === "function");
    };
    t2 = [isMobile];
    $[0] = isMobile;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = isMobile && !canUseSwiperTouchSurface;
  let t4;
  if ($[3] !== isBusy || $[4] !== isMobile || $[5] !== onAdvanceStart || $[6] !== onSkip || $[7] !== onVoteWithSwipe || $[8] !== swipeVoteAmount || $[9] !== t3) {
    t4 = {
      isBusy,
      isMobile,
      onAdvanceStart,
      onSkip,
      onVoteWithSwipe,
      swipeVoteAmount,
      usesTransitionlessSwipeCommit: t3
    };
    $[3] = isBusy;
    $[4] = isMobile;
    $[5] = onAdvanceStart;
    $[6] = onSkip;
    $[7] = onVoteWithSwipe;
    $[8] = swipeVoteAmount;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const swipeState = useQuickVotePreviewSwipeState(t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useLayoutEffect(_temp, t5);
  let t6;
  let t7;
  if ($[12] !== canUseSwiperTouchSurface || $[13] !== isBusy || $[14] !== isMobile || $[15] !== swipeState) {
    t6 = () => {
      if (canUseSwiperTouchSurface || !isMobile) {
        return;
      }
      const previewCardNode = previewCardRef.current;
      if (!previewCardNode) {
        return;
      }
      const fallbackTouchStartRef = swipeState.fallbackTouchStartXRef;
      const fallbackTouchCurrentRef = swipeState.fallbackTouchCurrentXRef;
      const isFallbackTouchActive = swipeState.isFallbackTouchActiveRef;
      const handleTouchStart = event => {
        if (isBusy || swipeState.swipeExitDirection !== null) {
          swipeState.resetFallbackTouch();
          return;
        }
        if (isFallbackTouchActive.current) {
          return;
        }
        const startX = getTouchEventClientX(event);
        if (startX === null) {
          return;
        }
        event.stopPropagation();
        fallbackTouchStartRef.current = startX;
        fallbackTouchCurrentRef.current = startX;
        isFallbackTouchActive.current = true;
        flushSync(() => {
          swipeState.setSwipeOffset(0);
        });
      };
      const handleTouchMove = event_0 => {
        if (!isFallbackTouchActive.current || swipeState.swipeExitDirection !== null) {
          return;
        }
        const startX_0 = fallbackTouchStartRef.current;
        const currentX = getTouchEventClientX(event_0);
        if (startX_0 === null || currentX === null) {
          return;
        }
        event_0.stopPropagation();
        fallbackTouchCurrentRef.current = currentX;
        flushSync(() => {
          swipeState.setSwipeOffset(Math.max(-MAX_SWIPE_OFFSET, Math.min(currentX - startX_0, MAX_SWIPE_OFFSET)));
        });
      };
      const handleTouchEnd = event_1 => {
        if (!isFallbackTouchActive.current) {
          return;
        }
        event_1.stopPropagation();
        if (isBusy) {
          swipeState.resetFallbackTouch();
          swipeState.resetSwipe();
          return;
        }
        const startX_1 = fallbackTouchStartRef.current;
        const endX = getTouchEventClientX(event_1) ?? fallbackTouchCurrentRef.current;
        if (startX_1 === null || endX === null) {
          swipeState.resetFallbackTouch();
          swipeState.resetSwipe();
          return;
        }
        swipeState.handleSwipeEnd(endX - startX_1);
      };
      const handleTouchCancel = event_2 => {
        if (!isFallbackTouchActive.current) {
          return;
        }
        event_2.stopPropagation();
        swipeState.resetFallbackTouch();
        swipeState.resetSwipe();
      };
      previewCardNode.addEventListener("touchstart", handleTouchStart);
      previewCardNode.addEventListener("touchmove", handleTouchMove);
      previewCardNode.addEventListener("touchend", handleTouchEnd);
      previewCardNode.addEventListener("touchcancel", handleTouchCancel);
      return () => {
        previewCardNode.removeEventListener("touchstart", handleTouchStart);
        previewCardNode.removeEventListener("touchmove", handleTouchMove);
        previewCardNode.removeEventListener("touchend", handleTouchEnd);
        previewCardNode.removeEventListener("touchcancel", handleTouchCancel);
      };
    };
    t7 = [canUseSwiperTouchSurface, isBusy, isMobile, previewCardRef, swipeState];
    $[12] = canUseSwiperTouchSurface;
    $[13] = isBusy;
    $[14] = isMobile;
    $[15] = swipeState;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[18] !== isMobile || $[19] !== swipeState.swipeExitDirection || $[20] !== swipeState.swipeOffset) {
    t8 = getCardTransform(isMobile, swipeState.swipeExitDirection, swipeState.swipeOffset);
    $[18] = isMobile;
    $[19] = swipeState.swipeExitDirection;
    $[20] = swipeState.swipeOffset;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== swipeState.swipeExitDirection || $[23] !== swipeState.swipeOffset) {
    t9 = getCardTransitionDuration(swipeState.swipeExitDirection, swipeState.swipeOffset);
    $[22] = swipeState.swipeExitDirection;
    $[23] = swipeState.swipeOffset;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== canUseSwiperTouchSurface || $[26] !== swipeState.handleCardTransitionEnd || $[27] !== swipeState.handleSwiperMove || $[28] !== swipeState.handleSwiperTouchEnd || $[29] !== swipeState.swipeOffset || $[30] !== t8 || $[31] !== t9) {
    t10 = {
      previewCardRef,
      canUseSwiperTouchSurface,
      cardTransform: t8,
      cardTransitionDuration: t9,
      swipeOffset: swipeState.swipeOffset,
      handleCardTransitionEnd: swipeState.handleCardTransitionEnd,
      handleSwiperMove: swipeState.handleSwiperMove,
      handleSwiperTouchEnd: swipeState.handleSwiperTouchEnd
    };
    $[25] = canUseSwiperTouchSurface;
    $[26] = swipeState.handleCardTransitionEnd;
    $[27] = swipeState.handleSwiperMove;
    $[28] = swipeState.handleSwiperTouchEnd;
    $[29] = swipeState.swipeOffset;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}
function _temp() {
  patchComputedStyleForFallbackSwipe();
}